import {
  CreateWorksheetProductInput,
  ProposalProduct,
  UpdateProposalWorksheetItemInput,
  UpdateWorksheetProductInput,
  WorksheetCostCategory,
  WorksheetCostRowInput as WorksheetCostRowGraphQLInput,
  WorksheetCoverageUnit,
  WorksheetHeaderUnit,
  WorksheetDensityUnit,
  WorksheetMaterialCalcMethod,
  WorksheetMaterialOutputUnit,
  WorksheetPricingBaseline,
  WorksheetPricingBaselineInput,
  WorksheetPricingMode,
  WorksheetProduct,
  WorksheetThicknessUnit,
} from "generated/graphql";
import { WorksheetCostRowInput, WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import {
  HelperResolution,
  mapHelpersFromApi,
  mapHelpersToApi,
  resolveHelpers,
} from "utils/worksheet/worksheetHelpers";
import {
  centsToDollarsInput,
  dollarsToCents,
  parseDecimalInput,
} from "utils/worksheet/worksheetNumbers";
import { enumToRaw, rawToEnum } from "./worksheetOptions";
import { newRowUuid } from "./worksheetFormItems";
import { getRowEffectiveQuantity } from "./worksheetFormCosts";

export const mapPricingBaselineFromApi = (
  baseline?: WorksheetPricingBaseline | null
): WorksheetItemInput["pricingBaseline"] =>
  baseline
    ? {
        pricingMode: baseline.pricingMode as WorksheetPricingMode,
        sellingRate: centsToDollarsInput(baseline.sellingRate),
        fixedTotal: centsToDollarsInput(baseline.fixedTotal),
        markupPercentage:
          baseline.markupPercentage != null ? String(baseline.markupPercentage) : "",
      }
    : null;

export const mapPricingBaselineToApi = (
  baseline?: WorksheetItemInput["pricingBaseline"]
): WorksheetPricingBaselineInput | null =>
  baseline
    ? {
        pricingMode: baseline.pricingMode,
        sellingRate: String(dollarsToCents(baseline.sellingRate)),
        fixedTotal: String(dollarsToCents(baseline.fixedTotal)),
        markupPercentage: parseDecimalInput(baseline.markupPercentage),
      }
    : null;

export const capturePricingBaseline = (
  values: WorksheetItemInput
): NonNullable<WorksheetItemInput["pricingBaseline"]> => ({
  pricingMode: values.pricingMode,
  sellingRate: values.sellingRate,
  fixedTotal: values.fixedTotal,
  markupPercentage: values.markupPercentage,
});

export const restorePricingBaseline = (
  baseline: NonNullable<WorksheetItemInput["pricingBaseline"]>
): Partial<WorksheetItemInput> => ({
  pricingMode: baseline.pricingMode,
  sellingRate: baseline.sellingRate,
  fixedTotal: baseline.fixedTotal,
  markupPercentage: baseline.markupPercentage,
  pricingBaseline: null,
  pricingOverrideField: "none",
});

const mapCostRowFromApi = (
  row: ProposalProduct["worksheetCostRows"][number] | WorksheetProduct["costRows"][number]
): WorksheetCostRowInput => ({
  id: row.id,
  category: row.category as WorksheetCostCategory,
  name: row.name,
  sourceType: row.sourceType ?? "",
  sourceId: row.sourceId != null ? String(row.sourceId) : "",
  sourceName: row.sourceName ?? "",
  sourcePlantId: row.sourcePlantId != null ? String(row.sourcePlantId) : "",
  sourcePlantName: row.sourcePlantName ?? "",
  quantity: row.quantity ? String(row.quantity) : "",
  unit: rawToEnum(row.unit) as WorksheetCostRowInput["unit"],
  rate: centsToDollarsInput(row.rate),
  trucksNeeded: row.trucksNeeded != null ? String(row.trucksNeeded) : "",
  hoursPerTruck: row.hoursPerTruck != null ? String(row.hoursPerTruck) : "",
  truckingUnit: row.truckingUnit ?? "",
  truckCapacity: row.truckCapacity != null ? String(row.truckCapacity) : "",
  rowUuid: row.rowUuid ?? newRowUuid(),
  quantitySource: row.quantitySource ?? "manual",
  haulMaterialRowUuid: row.haulMaterialRowUuid ?? "",
  haulAllocationPercent: row.haulAllocationPercent != null ? String(row.haulAllocationPercent) : "",
  haulCapacityUnit: row.haulCapacityUnit ?? "Tons",
  haulRoundTripHours: row.haulRoundTripHours != null ? String(row.haulRoundTripHours) : "",
  haulLoadHours: row.haulLoadHours != null ? String(row.haulLoadHours) : "",
  haulUnloadHours: row.haulUnloadHours != null ? String(row.haulUnloadHours) : "",
  haulPlanningHours: row.haulPlanningHours != null ? String(row.haulPlanningHours) : "",
  haulMaxTripsPerDay: row.haulMaxTripsPerDay != null ? String(row.haulMaxTripsPerDay) : "",
  haulQuantityMode: row.haulQuantityMode ?? "",
  haulQuantity: row.haulQuantity != null ? String(row.haulQuantity) : "",
  haulUnit: row.haulUnit ?? "Tons",
  haulFullLoads: row.haulFullLoads ?? false,
  truckRateHours: centsToDollarsInput(row.truckRateHours),
  truckRateTons: centsToDollarsInput(row.truckRateTons),
  truckRateYards: centsToDollarsInput(row.truckRateYards),
  truckRateLoads: centsToDollarsInput(row.truckRateLoads),
  calcMethod: (rawToEnum(row.calcMethod) ||
    WorksheetMaterialCalcMethod.MANUAL) as WorksheetMaterialCalcMethod,
  jobSizeOverride: row.jobSizeOverride != null ? String(row.jobSizeOverride) : "",
  jobSizeUnit: rawToEnum(row.jobSizeUnit) as WorksheetCostRowInput["jobSizeUnit"],
  coverageRate: row.coverageRate != null ? String(row.coverageRate) : "",
  coverageUnit: (rawToEnum(row.coverageUnit) ||
    WorksheetCoverageUnit.SQUARE_FOOT) as WorksheetCoverageUnit,
  thickness: row.thickness != null ? String(row.thickness) : "",
  thicknessUnit: (rawToEnum(row.thicknessUnit) ||
    WorksheetThicknessUnit.INCH) as WorksheetThicknessUnit,
  density: row.density != null ? String(row.density) : "",
  densityUnit: (rawToEnum(row.densityUnit) ||
    WorksheetDensityUnit.LB_PER_CUFT) as WorksheetDensityUnit,
  weightFactor: row.weightFactor != null ? String(row.weightFactor) : "",
  outputUnit: (rawToEnum(row.outputUnit) ||
    WorksheetMaterialOutputUnit.TON) as WorksheetMaterialOutputUnit,
  quantityOverridden: row.quantityOverridden,
});

export const getProposalItemFormValues = (product: ProposalProduct): WorksheetItemInput => ({
  id: product.id,
  name: product.productName ?? product.material?.name ?? "New Item",
  statementOfWork: product.statementOfWork ?? product.material?.statementOfWork ?? "",
  unit: (rawToEnum(product.worksheetUnit) || WorksheetHeaderUnit.EACH) as WorksheetHeaderUnit,
  sellingQuantity: product.sellingQuantity ? String(product.sellingQuantity) : "",
  sellingRate: centsToDollarsInput(product.sellingRate),
  fixedTotal: centsToDollarsInput(product.fixedTotal),
  pricingMode: product.pricingMode as WorksheetPricingMode,
  pricingOverrideField: product.pricingOverrideField ?? "none",
  pricingBaseline: mapPricingBaselineFromApi(product.pricingBaseline),
  internalName: "",
  discount: centsToDollarsInput(product.discount),
  helpers: mapHelpersFromApi(product.helpers),
  markupPercentage:
    product.markupPercentage != null && Number(product.markupPercentage) !== 0
      ? String(product.markupPercentage)
      : "",
  overheadPercentage:
    product.overheadPercentage != null && Number(product.overheadPercentage) !== 0
      ? String(product.overheadPercentage)
      : "",
  indirectPercentage:
    product.indirectPercentage != null && Number(product.indirectPercentage) !== 0
      ? String(product.indirectPercentage)
      : "",
  isOptional: product.isOptional,
  costRows: (product.worksheetCostRows ?? [])
    .slice()
    .sort((a, b) => a.ordering - b.ordering)
    .map(mapCostRowFromApi),
});

export const getWorksheetProductFormValues = (product: WorksheetProduct): WorksheetItemInput => ({
  id: product.id,
  name: product.name,
  statementOfWork: product.statementOfWork ?? "",
  unit: (rawToEnum(product.unit) || WorksheetHeaderUnit.EACH) as WorksheetHeaderUnit,
  sellingQuantity: product.sellingQuantity ? String(product.sellingQuantity) : "",
  sellingRate: centsToDollarsInput(product.sellingRate),
  fixedTotal: centsToDollarsInput(product.fixedTotal),
  pricingMode: product.pricingMode as WorksheetPricingMode,
  pricingOverrideField: product.pricingOverrideField ?? "none",
  pricingBaseline: mapPricingBaselineFromApi(product.pricingBaseline),
  internalName: product.internalName ?? "",
  discount: "",
  helpers: mapHelpersFromApi(product.helpers),
  markupPercentage:
    product.markupPercentage != null && Number(product.markupPercentage) !== 0
      ? String(product.markupPercentage)
      : "",
  overheadPercentage:
    product.overheadPercentage != null && Number(product.overheadPercentage) !== 0
      ? String(product.overheadPercentage)
      : "",
  indirectPercentage:
    product.indirectPercentage != null && Number(product.indirectPercentage) !== 0
      ? String(product.indirectPercentage)
      : "",
  isOptional: false,
  organizationProductTypeId: product.organizationProductType?.id ?? "",
  costRows: (product.costRows ?? [])
    .slice()
    .sort((a, b) => a.ordering - b.ordering)
    .map(mapCostRowFromApi),
});

const mapCostRowToApi = (
  row: WorksheetCostRowInput,
  ordering: number,
  lineQuantity: string,
  lineUnit: WorksheetHeaderUnit,
  resolution?: HelperResolution
): WorksheetCostRowGraphQLInput => ({
  category: row.category,
  ordering,
  name: row.name || "Item",
  sourceType: row.sourceType || null,
  sourceId: row.sourceId || null,
  sourceName: row.sourceName || null,
  sourcePlantId: row.sourcePlantId || null,
  sourcePlantName: row.sourcePlantName || null,
  quantity: getRowEffectiveQuantity(row, lineQuantity, lineUnit, resolution),
  unit: row.unit || null,
  rate: dollarsToCents(row.rate),
  trucksNeeded:
    row.category === WorksheetCostCategory.TRUCKING ? parseDecimalInput(row.trucksNeeded) : null,
  hoursPerTruck:
    row.category === WorksheetCostCategory.TRUCKING ? parseDecimalInput(row.hoursPerTruck) : null,
  truckingUnit: row.category === WorksheetCostCategory.TRUCKING ? row.truckingUnit || null : null,
  truckCapacity:
    row.category === WorksheetCostCategory.TRUCKING && row.truckCapacity !== ""
      ? Number(row.truckCapacity)
      : null,
  truckRateHours:
    row.category === WorksheetCostCategory.TRUCKING && row.truckRateHours !== ""
      ? dollarsToCents(row.truckRateHours)
      : null,
  truckRateTons:
    row.category === WorksheetCostCategory.TRUCKING && row.truckRateTons !== ""
      ? dollarsToCents(row.truckRateTons)
      : null,
  truckRateYards:
    row.category === WorksheetCostCategory.TRUCKING && row.truckRateYards !== ""
      ? dollarsToCents(row.truckRateYards)
      : null,
  truckRateLoads:
    row.category === WorksheetCostCategory.TRUCKING && row.truckRateLoads !== ""
      ? dollarsToCents(row.truckRateLoads)
      : null,
  rowUuid: row.rowUuid || newRowUuid(),
  quantitySource: row.quantitySource || "manual",
  haulMaterialRowUuid: row.haulMaterialRowUuid || null,
  haulAllocationPercent:
    row.haulAllocationPercent === "" ? null : parseDecimalInput(row.haulAllocationPercent),
  haulCapacityUnit: row.haulCapacityUnit || null,
  haulRoundTripHours:
    row.haulRoundTripHours === "" ? null : parseDecimalInput(row.haulRoundTripHours),
  haulLoadHours: row.haulLoadHours === "" ? null : parseDecimalInput(row.haulLoadHours),
  haulUnloadHours: row.haulUnloadHours === "" ? null : parseDecimalInput(row.haulUnloadHours),
  haulPlanningHours: row.haulPlanningHours === "" ? null : parseDecimalInput(row.haulPlanningHours),
  haulMaxTripsPerDay: row.haulMaxTripsPerDay === "" ? null : Number(row.haulMaxTripsPerDay),
  haulQuantityMode: row.haulQuantityMode || null,
  haulQuantity: row.haulQuantity === "" ? null : parseDecimalInput(row.haulQuantity),
  haulUnit: row.haulUnit || null,
  haulFullLoads: row.haulFullLoads,
  calcMethod:
    row.category === WorksheetCostCategory.MATERIAL
      ? row.calcMethod
      : WorksheetMaterialCalcMethod.MANUAL,
  jobSizeOverride: row.jobSizeOverride === "" ? null : parseDecimalInput(row.jobSizeOverride),
  jobSizeUnit: row.jobSizeUnit ? enumToRaw(row.jobSizeUnit) : null,
  coverageRate: row.coverageRate === "" ? null : parseDecimalInput(row.coverageRate),
  coverageUnit: row.coverageUnit,
  thickness: row.thickness === "" ? null : parseDecimalInput(row.thickness),
  thicknessUnit: row.thicknessUnit,
  density: row.density === "" ? null : parseDecimalInput(row.density),
  densityUnit: row.densityUnit,
  weightFactor: row.weightFactor === "" ? null : parseDecimalInput(row.weightFactor),
  outputUnit: row.outputUnit,
  quantityOverridden: row.quantityOverridden,
});

export const formValuesToProposalWorksheetInput = (
  values: WorksheetItemInput
): UpdateProposalWorksheetItemInput => {
  const resolution = resolveHelpers(values);

  return {
    id: values.id!,
    name: values.name || "New Item",
    statementOfWork: values.statementOfWork || null,
    unit: values.unit,
    sellingQuantity: parseDecimalInput(values.sellingQuantity),
    sellingRate: dollarsToCents(values.sellingRate),
    fixedTotal: String(dollarsToCents(values.fixedTotal)),
    pricingMode: values.pricingMode,
    pricingOverrideField: values.pricingOverrideField || "none",
    pricingBaseline: mapPricingBaselineToApi(values.pricingBaseline),
    markupPercentage: parseDecimalInput(values.markupPercentage),
    overheadPercentage: parseDecimalInput(values.overheadPercentage),
    indirectPercentage: parseDecimalInput(values.indirectPercentage),
    discount: values.discount === "" ? null : dollarsToCents(values.discount),
    helpers: mapHelpersToApi(values.helpers),
    isOptional: values.isOptional,
    costRows: values.costRows.map((row, index) =>
      mapCostRowToApi(row, index, values.sellingQuantity, values.unit, resolution)
    ),
  };
};

export const formValuesToWorksheetProductInput = (
  values: WorksheetItemInput,
  options: { includeLibraryMeta?: boolean } = {}
): UpdateWorksheetProductInput | CreateWorksheetProductInput => {
  const resolution = resolveHelpers(values);

  return {
    ...(values.id ? { id: values.id } : {}),
    name: values.name || "New Item",
    statementOfWork: values.statementOfWork || null,
    unit: values.unit,
    sellingQuantity: parseDecimalInput(values.sellingQuantity),
    sellingRate: dollarsToCents(values.sellingRate),
    fixedTotal: String(dollarsToCents(values.fixedTotal)),
    pricingMode: values.pricingMode,
    pricingOverrideField: values.pricingOverrideField || "none",
    pricingBaseline: mapPricingBaselineToApi(values.pricingBaseline),
    markupPercentage: parseDecimalInput(values.markupPercentage),
    overheadPercentage: parseDecimalInput(values.overheadPercentage),
    indirectPercentage: parseDecimalInput(values.indirectPercentage),
    ...(options.includeLibraryMeta
      ? {
          internalName: values.internalName || null,
          organizationProductTypeId: values.organizationProductTypeId || null,
        }
      : {}),
    helpers: mapHelpersToApi(values.helpers),
    costRows: values.costRows.map((row, index) =>
      mapCostRowToApi(row, index, values.sellingQuantity, values.unit, resolution)
    ),
  };
};
